import * as React from "react"
import { motion, AnimatePresence } from "motion/react"
import {
  Boxes,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Upload,
  Image as ImageIcon,
  Star,
  StarOff,
  Eye,
  EyeOff,
  Trash2,
  Package,
  AlertTriangle,
  Search,
  Tag,
  DollarSign,
  Hash,
  BarChart3,
  ShoppingBag,
  Layers,
  X,
  Check,
  Info,
  ImagePlus,
  Sparkles,
  TrendingDown,
  Archive,
} from "lucide-react"
import { useAuth, type StoreProduct } from "@/contexts/AuthContext"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"

/* ─── Types ─── */

interface ProductDraft {
  _id?: string
  name: string
  description: string
  category: string
  sku: string
  badge: string
  priceUsd: string
  stock: string
  maxPerOrder: string
  isActive: boolean
  featured: boolean
  imageUrls: string[]
}

const emptyDraft = (): ProductDraft => ({
  name: "",
  description: "",
  category: "General",
  sku: "",
  badge: "",
  priceUsd: "0.00",
  stock: "0",
  maxPerOrder: "",
  isActive: true,
  featured: false,
  imageUrls: [],
})

const toDraft = (product: StoreProduct): ProductDraft => ({
  _id: product._id,
  name: product.name,
  description: product.description,
  category: product.category || "General",
  sku: product.sku || "",
  badge: product.badge || "",
  priceUsd: product.priceUsd.toFixed(2),
  stock: String(product.stock),
  maxPerOrder: product.maxPerOrder ? String(product.maxPerOrder) : "",
  isActive: product.isActive,
  featured: product.featured,
  imageUrls: [...product.imageUrls],
})

const currency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value)

/* ─── Shared sub-components ─── */

function StatCard({
  label,
  value,
  color,
  icon: Icon,
  delay,
}: {
  label: string
  value: number
  color: string
  icon: React.ElementType
  delay: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="group relative overflow-hidden rounded-xl border border-[#2A2A45] bg-[#0C0C12] p-4 hover:border-[#2A2A3A] transition-colors"
    >
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `radial-gradient(120px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), ${color}08, transparent)` }} />
      <div className="relative flex items-center justify-between">
        <div>
          <p className="text-[11px] text-white/35 uppercase tracking-wider font-admin mb-1.5">{label}</p>
          <p className="text-2xl font-semibold font-admin" style={{ color }}>{value}</p>
        </div>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}12` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
    </motion.div>
  )
}

function FormField({
  label,
  tooltip,
  optional,
  children,
}: {
  label: string
  tooltip?: string
  optional?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <label className="text-xs font-medium text-white/50 font-admin">{label}</label>
        {optional && <span className="text-[10px] text-white/20 font-admin">optional</span>}
        {tooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-3 h-3 text-white/20 cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[200px]">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      {children}
    </div>
  )
}

const inputClass =
  "w-full px-3 py-2.5 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/85 font-admin placeholder:text-white/15 outline-none focus:border-[#F37020]/50 focus:ring-1 focus:ring-[#F37020]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"

/* ─── Main ─── */

export function AdminStore() {
  const { user, getAdminStoreProducts, saveAdminStoreProduct, getPresignedUrl } = useAuth()
  const [products, setProducts] = React.useState<StoreProduct[]>([])
  const [loading, setLoading] = React.useState(true)
  const [refreshing, setRefreshing] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [uploading, setUploading] = React.useState(false)
  const [draft, setDraft] = React.useState<ProductDraft>(emptyDraft())
  const [modalOpen, setModalOpen] = React.useState(false)
  const [feedback, setFeedback] = React.useState<{ type: "success" | "error"; text: string } | null>(null)
  const [searchQuery, setSearchQuery] = React.useState("")
  const [dragOver, setDragOver] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const isSuperAdmin = user?.role === "super_admin"

  const loadProducts = React.useCallback(async (silent?: boolean) => {
    if (!silent) setLoading(true)
    setFeedback(null)
    const result = await getAdminStoreProducts()
    if (result.success && result.products) {
      setProducts(result.products)
    } else {
      setFeedback({ type: "error", text: result.message || "Failed to load store products" })
    }
    if (!silent) setLoading(false)
  }, [getAdminStoreProducts])

  React.useEffect(() => {
    loadProducts()
  }, [loadProducts])

  // Auto-dismiss feedback after 4 seconds
  React.useEffect(() => {
    if (!feedback) return
    const timer = setTimeout(() => setFeedback(null), 4000)
    return () => clearTimeout(timer)
  }, [feedback])

  const handleRefresh = async () => {
    setRefreshing(true)
    await loadProducts(true)
    setRefreshing(false)
  }

  const openEditModal = (product: StoreProduct) => {
    setDraft(toDraft(product))
    setFeedback(null)
    setModalOpen(true)
  }

  const openNewModal = () => {
    setDraft(emptyDraft())
    setFeedback(null)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
  }

  const updateDraft = <K extends keyof ProductDraft>(key: K, value: ProductDraft[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }))
  }

  const activeCount = products.filter((p) => p.isActive).length
  const lowStockCount = products.filter((p) => p.isActive && p.stock > 0 && p.stock <= 5).length
  const outOfStockCount = products.filter((p) => p.isActive && p.stock <= 0).length

  const filteredProducts = React.useMemo(() => {
    if (!searchQuery.trim()) return products
    const q = searchQuery.toLowerCase()
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        (p.sku && p.sku.toLowerCase().includes(q))
    )
  }, [products, searchQuery])

  const processFiles = async (files: File[]) => {
    const validFiles = files.filter((file) => file.type.startsWith("image/"))
    if (validFiles.length === 0) {
      setFeedback({ type: "error", text: "Please select image files only." })
      return
    }

    setUploading(true)
    setFeedback(null)

    try {
      const uploadResults = await Promise.all(
        validFiles.slice(0, Math.max(0, 8 - draft.imageUrls.length)).map(async (file) => {
          const presigned = await getPresignedUrl(file.name, file.type)
          if (!presigned.success || !presigned.uploadUrl || !presigned.fileUrl) {
            throw new Error(presigned.message || `Failed to get upload URL for ${file.name}`)
          }

          const uploadRes = await fetch(presigned.uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": file.type },
            body: file,
          })

          if (!uploadRes.ok) {
            throw new Error(`Upload failed for ${file.name}`)
          }

          return presigned.fileUrl
        })
      )

      setDraft((prev) => ({
        ...prev,
        imageUrls: Array.from(new Set([...prev.imageUrls, ...uploadResults])).slice(0, 8),
      }))
      setFeedback({ type: "success", text: `Uploaded ${uploadResults.length} image${uploadResults.length === 1 ? "" : "s"}.` })
    } catch (error: any) {
      setFeedback({ type: "error", text: error?.message || "Image upload failed" })
    }

    setUploading(false)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const handleUploadImages = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    if (files.length === 0) return
    await processFiles(files)
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (!isSuperAdmin) return
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) await processFiles(files)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (isSuperAdmin) setDragOver(true)
  }

  const removeImage = (url: string) => {
    setDraft((prev) => ({ ...prev, imageUrls: prev.imageUrls.filter((imageUrl) => imageUrl !== url) }))
  }

  const canSave =
    isSuperAdmin &&
    !saving &&
    !uploading &&
    draft.name.trim().length > 0 &&
    Number.isFinite(Number(draft.priceUsd)) &&
    Number(draft.priceUsd) >= 0 &&
    Number.isFinite(Number(draft.stock)) &&
    Number(draft.stock) >= 0

  const handleSave = async () => {
    if (!isSuperAdmin) {
      setFeedback({ type: "error", text: "Only super admins can create or edit store products." })
      return
    }

    const priceUsd = Number(draft.priceUsd)
    const stock = Number(draft.stock)
    const maxPerOrder = draft.maxPerOrder.trim() ? Number(draft.maxPerOrder) : null

    if (!draft.name.trim()) {
      setFeedback({ type: "error", text: "Product name is required." })
      return
    }
    if (!Number.isFinite(priceUsd) || priceUsd < 0) {
      setFeedback({ type: "error", text: "Price must be a valid number." })
      return
    }
    if (!Number.isFinite(stock) || stock < 0 || Math.trunc(stock) !== stock) {
      setFeedback({ type: "error", text: "Stock must be a whole number." })
      return
    }
    if (maxPerOrder != null && (!Number.isFinite(maxPerOrder) || maxPerOrder < 1 || Math.trunc(maxPerOrder) !== maxPerOrder)) {
      setFeedback({ type: "error", text: "Max per order must be a whole number of at least 1." })
      return
    }

    setSaving(true)
    setFeedback(null)

    const response = await saveAdminStoreProduct({
      _id: draft._id,
      name: draft.name.trim(),
      description: draft.description.trim(),
      category: draft.category.trim() || "General",
      sku: draft.sku.trim(),
      badge: draft.badge.trim(),
      priceUsd,
      stock,
      imageUrls: draft.imageUrls,
      isActive: draft.isActive,
      featured: draft.featured,
      maxPerOrder: maxPerOrder == null ? null : maxPerOrder,
    })

    if (response.success && response.product) {
      setFeedback({ type: "success", text: response.message || "Product saved." })
      setDraft(toDraft(response.product))
      setProducts((prev) => {
        const exists = prev.some((product) => product._id === response.product!._id)
        const next = exists
          ? prev.map((product) => (product._id === response.product!._id ? response.product! : product))
          : [response.product!, ...prev]
        return next.sort((a, b) => {
          if (a.isActive !== b.isActive) return a.isActive ? -1 : 1
          if (a.featured !== b.featured) return a.featured ? -1 : 1
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        })
      })
      setModalOpen(false)
    } else {
      setFeedback({ type: "error", text: response.message || "Failed to save product." })
    }

    setSaving(false)
  }

  /* ─── Loading State ─── */
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#F37020]/30 border-t-[#F37020] rounded-full animate-spin mx-auto" />
          <p className="text-xs text-white/30 mt-3 font-admin">Loading store catalog...</p>
        </div>
      </div>
    )
  }

  const previewPrice = Number.isFinite(Number(draft.priceUsd)) ? Number(draft.priceUsd) : 0

  return (
    <div className="space-y-6">
      {/* Toast feedback */}
      <AnimatePresence>
        {feedback && (
          <motion.div
            initial={{ opacity: 0, y: -20, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: -20, x: "-50%" }}
            className="fixed top-4 left-1/2 z-[60]"
          >
            <div
              className={[
                "flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium font-admin shadow-2xl backdrop-blur-sm",
                feedback.type === "success"
                  ? "bg-emerald-500/15 border-emerald-500/25 text-emerald-400"
                  : "bg-red-500/15 border-red-500/25 text-red-400",
              ].join(" ")}
            >
              {feedback.type === "success" ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
              {feedback.text}
              <button type="button" onClick={() => setFeedback(null)} className="ml-2 opacity-50 hover:opacity-100">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header with permission notice */}
      {!isSuperAdmin && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3"
        >
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-xs text-amber-300/90 font-admin">
            View-only mode. Ask a super admin to publish product changes.
          </p>
        </motion.div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard label="Active Products" value={activeCount} color="#22c55e" icon={Package} delay={0} />
        <StatCard label="Low Stock" value={lowStockCount} color="#f59e0b" icon={TrendingDown} delay={0.05} />
        <StatCard label="Out of Stock" value={outOfStockCount} color="#ef4444" icon={Archive} delay={0.1} />
      </div>

      {/* ─── Product List (full width) ─── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-[#111117] border border-[#2A2A45] rounded-xl overflow-hidden"
      >
        {/* List Header */}
        <div className="p-4 border-b border-[#2A2A45]">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="text-sm font-semibold text-white/80 font-admin flex items-center gap-2">
              <Layers className="w-4 h-4 text-white/30" />
              Products
              <span className="text-[10px] bg-white/[0.06] text-white/35 px-1.5 py-0.5 rounded-md font-admin">{products.length}</span>
            </h3>
            <div className="flex items-center gap-1.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="w-8 h-8 rounded-lg bg-[#0C0C12] border border-[#2A2A45] inline-flex items-center justify-center text-white/40 hover:text-white/70 hover:border-[#2A2A3A] transition-colors disabled:opacity-50"
                  >
                    {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent>Refresh catalog</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={openNewModal}
                    disabled={!isSuperAdmin}
                    className="w-8 h-8 rounded-lg bg-[#F37020]/15 border border-[#F37020]/25 inline-flex items-center justify-center text-[#F37020] hover:bg-[#F37020]/25 transition-colors disabled:opacity-40"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>New product</TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/20" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="w-full pl-8 pr-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-xs text-white/70 font-admin placeholder:text-white/15 outline-none focus:border-[#F37020]/40 transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/25 hover:text-white/50"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Product Grid */}
        <div className="p-4">
          {filteredProducts.length === 0 && products.length > 0 ? (
            <div className="py-10 text-center">
              <Search className="w-6 h-6 text-white/15 mx-auto" />
              <p className="text-xs text-white/25 mt-2 font-admin">No matches for "{searchQuery}"</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="py-10 text-center">
              <ShoppingBag className="w-6 h-6 text-white/15 mx-auto" />
              <p className="text-sm text-white/25 mt-2 font-admin">No products yet</p>
              {isSuperAdmin && (
                <button
                  type="button"
                  onClick={openNewModal}
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#F37020]/15 border border-[#F37020]/25 text-sm text-[#F37020] hover:bg-[#F37020]/25 transition-colors font-admin"
                >
                  <Plus className="w-4 h-4" />
                  Create your first product
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {filteredProducts.map((product) => (
                <button
                  key={product._id}
                  type="button"
                  onClick={() => openEditModal(product)}
                  className="group text-left rounded-xl border border-[#2A2A45] bg-[#0C0C12] overflow-hidden hover:border-[#2A2A3A] hover:bg-[#0E0E14] transition-all"
                >
                  {/* Image */}
                  <div className="h-32 bg-gradient-to-br from-[#111117] to-[#0C0C12] flex items-center justify-center relative overflow-hidden">
                    {product.primaryImageUrl ? (
                      <img src={product.primaryImageUrl} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <ImageIcon className="w-6 h-6 text-white/10" />
                    )}
                    {product.featured && (
                      <div className="absolute top-2 right-2 flex items-center gap-1 rounded-md bg-amber-500/90 text-black px-1.5 py-0.5 text-[10px] font-bold font-admin">
                        <Star className="w-2.5 h-2.5" fill="currentColor" />
                      </div>
                    )}
                    {!product.isActive && (
                      <div className="absolute top-2 left-2 rounded-md bg-white/10 backdrop-blur-sm text-white/60 px-1.5 py-0.5 text-[10px] font-bold font-admin">
                        Draft
                      </div>
                    )}
                    {product.badge && (
                      <div className="absolute bottom-2 left-2 rounded-md bg-[#9B59B6] text-white px-1.5 py-0.5 text-[10px] font-bold font-admin">
                        {product.badge}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <p className="text-sm font-medium text-white/85 font-admin truncate group-hover:text-white transition-colors">
                      {product.name}
                    </p>
                    <p className="text-[11px] text-white/30 font-admin mt-0.5">{product.category}</p>
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-sm font-semibold text-white/80 font-admin">{currency(product.priceUsd)}</p>
                      <p className={[
                        "text-[11px] font-medium font-admin",
                        product.stock <= 0 ? "text-red-400/80" : product.stock <= 5 ? "text-amber-400/80" : "text-emerald-400/60",
                      ].join(" ")}>
                        {product.stock <= 0 ? "Out of stock" : `${product.stock} left`}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {/* ─── Product Modal ─── */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
            onClick={(e) => { if (e.target === e.currentTarget) closeModal() }}
          >
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />

            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              className="relative w-full max-w-3xl mx-4 my-8 bg-[#111117] border border-[#2A2A45] rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-[#2A2A45]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#F37020]/20 to-[#F37020]/5 border border-[#F37020]/20 flex items-center justify-center">
                    {draft._id ? <Boxes className="w-5 h-5 text-[#F37020]/80" /> : <Sparkles className="w-5 h-5 text-[#F37020]/80" />}
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-white/90 font-admin">
                      {draft._id ? "Edit Product" : "New Product"}
                    </h3>
                    <p className="text-xs text-white/30 font-admin mt-0.5">
                      {draft._id ? `Editing "${draft.name || "Untitled"}"` : "Fill in the details to create a new store product"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeModal}
                  className="w-8 h-8 rounded-lg bg-white/[0.04] border border-[#2A2A45] inline-flex items-center justify-center text-white/40 hover:text-white/70 hover:bg-white/[0.08] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-5 max-h-[calc(100vh-12rem)] overflow-y-auto space-y-5">
                {/* Basic Info Section */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="w-4 h-4 text-white/25" />
                    <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider font-admin">Basic Information</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="Product Name" tooltip="Displayed to customers in the store">
                      <input
                        value={draft.name}
                        onChange={(e) => updateDraft("name", e.target.value)}
                        maxLength={160}
                        disabled={!isSuperAdmin}
                        placeholder="Oval Hoodie"
                        className={inputClass}
                      />
                    </FormField>
                    <FormField label="Category" tooltip="Used for grouping in the catalog">
                      <input
                        value={draft.category}
                        onChange={(e) => updateDraft("category", e.target.value)}
                        maxLength={80}
                        disabled={!isSuperAdmin}
                        placeholder="Merch"
                        className={inputClass}
                      />
                    </FormField>
                    <FormField label="SKU" optional tooltip="Internal tracking code">
                      <input
                        value={draft.sku}
                        onChange={(e) => updateDraft("sku", e.target.value)}
                        maxLength={80}
                        disabled={!isSuperAdmin}
                        placeholder="HOODIE-BLK-L"
                        className={inputClass}
                      />
                    </FormField>
                    <FormField label="Badge" optional tooltip="Shows a badge on the product card (e.g. Best Seller, New)">
                      <input
                        value={draft.badge}
                        onChange={(e) => updateDraft("badge", e.target.value)}
                        maxLength={40}
                        disabled={!isSuperAdmin}
                        placeholder="Best Seller"
                        className={inputClass}
                      />
                    </FormField>
                  </div>

                  <div className="mt-4">
                    <FormField label="Description" optional tooltip="Shown on the product page. Supports plain text.">
                      <textarea
                        value={draft.description}
                        onChange={(e) => updateDraft("description", e.target.value)}
                        rows={3}
                        maxLength={4000}
                        disabled={!isSuperAdmin}
                        placeholder="Product details, sizes, color variants, what's included..."
                        className={`${inputClass} resize-y min-h-[80px]`}
                      />
                    </FormField>
                    <p className="text-[10px] text-white/15 font-admin mt-1 text-right">{draft.description.length}/4000</p>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-[#2A2A45]" />

                {/* Pricing & Inventory Section */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <DollarSign className="w-4 h-4 text-white/25" />
                    <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider font-admin">Pricing & Inventory</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField label="Price (USD)">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25 text-sm font-admin">$</span>
                        <input
                          value={draft.priceUsd}
                          onChange={(e) => updateDraft("priceUsd", e.target.value)}
                          inputMode="decimal"
                          disabled={!isSuperAdmin}
                          placeholder="49.99"
                          className={`${inputClass} pl-7`}
                        />
                      </div>
                    </FormField>
                    <FormField label="Stock" tooltip="Decremented on each successful checkout">
                      <div className="relative">
                        <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/20" />
                        <input
                          value={draft.stock}
                          onChange={(e) => updateDraft("stock", e.target.value)}
                          inputMode="numeric"
                          disabled={!isSuperAdmin}
                          placeholder="100"
                          className={`${inputClass} pl-8`}
                        />
                      </div>
                    </FormField>
                    <FormField label="Max Per Order" optional tooltip="Limits how many a customer can buy at once">
                      <input
                        value={draft.maxPerOrder}
                        onChange={(e) => updateDraft("maxPerOrder", e.target.value)}
                        inputMode="numeric"
                        disabled={!isSuperAdmin}
                        placeholder="No limit"
                        className={inputClass}
                      />
                    </FormField>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-[#2A2A45]" />

                {/* Visibility & Merchandising */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <BarChart3 className="w-4 h-4 text-white/25" />
                    <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider font-admin">Visibility & Merchandising</h4>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => updateDraft("isActive", !draft.isActive)}
                      disabled={!isSuperAdmin}
                      className={[
                        "flex items-center gap-3 rounded-xl border px-4 py-3 transition-all disabled:opacity-50 disabled:cursor-not-allowed",
                        draft.isActive
                          ? "bg-emerald-500/8 border-emerald-500/25 hover:border-emerald-500/40"
                          : "bg-[#0C0C12] border-[#2A2A45] hover:border-white/15",
                      ].join(" ")}
                    >
                      <div className={[
                        "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                        draft.isActive ? "bg-emerald-500/15 text-emerald-400" : "bg-white/[0.04] text-white/25",
                      ].join(" ")}>
                        {draft.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </div>
                      <div className="text-left">
                        <p className={[
                          "text-sm font-medium font-admin transition-colors",
                          draft.isActive ? "text-emerald-400" : "text-white/40",
                        ].join(" ")}>
                          {draft.isActive ? "Visible" : "Hidden"}
                        </p>
                        <p className="text-[11px] text-white/25 font-admin">
                          {draft.isActive ? "Shown in customer store" : "Draft, not visible"}
                        </p>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => updateDraft("featured", !draft.featured)}
                      disabled={!isSuperAdmin}
                      className={[
                        "flex items-center gap-3 rounded-xl border px-4 py-3 transition-all disabled:opacity-50 disabled:cursor-not-allowed",
                        draft.featured
                          ? "bg-amber-500/8 border-amber-500/25 hover:border-amber-500/40"
                          : "bg-[#0C0C12] border-[#2A2A45] hover:border-white/15",
                      ].join(" ")}
                    >
                      <div className={[
                        "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                        draft.featured ? "bg-amber-500/15 text-amber-400" : "bg-white/[0.04] text-white/25",
                      ].join(" ")}>
                        {draft.featured ? <Star className="w-4 h-4" fill="currentColor" /> : <StarOff className="w-4 h-4" />}
                      </div>
                      <div className="text-left">
                        <p className={[
                          "text-sm font-medium font-admin transition-colors",
                          draft.featured ? "text-amber-400" : "text-white/40",
                        ].join(" ")}>
                          {draft.featured ? "Featured" : "Standard"}
                        </p>
                        <p className="text-[11px] text-white/25 font-admin">
                          {draft.featured ? "Highlighted with star badge" : "Regular catalog listing"}
                        </p>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-[#2A2A45]" />

                {/* Images Section */}
                <div>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <ImageIcon className="w-4 h-4 text-white/25" />
                      <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider font-admin">Product Images</h4>
                      <span className="text-[10px] bg-white/[0.06] text-white/30 px-1.5 py-0.5 rounded-md font-admin">{draft.imageUrls.length}/8</span>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleUploadImages}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={!isSuperAdmin || uploading || draft.imageUrls.length >= 8}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#F37020]/10 border border-[#F37020]/20 text-xs text-[#F9A167] hover:bg-[#F37020]/20 transition-colors disabled:opacity-40 font-admin"
                    >
                      {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {uploading ? "Uploading..." : "Upload"}
                    </button>
                  </div>

                  {/* Drop zone or image grid */}
                  {draft.imageUrls.length === 0 ? (
                    <div
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={() => setDragOver(false)}
                      onClick={() => isSuperAdmin && fileInputRef.current?.click()}
                      className={[
                        "rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-all",
                        dragOver
                          ? "border-[#F37020]/50 bg-[#F37020]/5"
                          : "border-[#2A2A45] hover:border-[#2A2A3A] bg-[#0C0C12]/50",
                      ].join(" ")}
                    >
                      <ImagePlus className={[
                        "w-8 h-8 mx-auto transition-colors",
                        dragOver ? "text-[#F37020]/60" : "text-white/12",
                      ].join(" ")} />
                      <p className="text-xs text-white/30 mt-3 font-admin">
                        {dragOver ? "Drop to upload" : "Drag & drop images here, or click to browse"}
                      </p>
                      <p className="text-[10px] text-white/15 mt-1 font-admin">PNG, JPG, WebP up to 8 images. First image is the cover.</p>
                    </div>
                  ) : (
                    <div
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={() => setDragOver(false)}
                      className={[
                        "grid grid-cols-2 md:grid-cols-4 gap-3 rounded-xl p-1 transition-all",
                        dragOver ? "ring-2 ring-[#F37020]/30 bg-[#F37020]/5" : "",
                      ].join(" ")}
                    >
                      {draft.imageUrls.map((imageUrl, index) => (
                        <div
                          key={imageUrl}
                          className="group/img relative rounded-lg overflow-hidden border border-[#2A2A45] bg-[#0C0C12] aspect-square"
                        >
                          <img src={imageUrl} alt={`Product image ${index + 1}`} className="w-full h-full object-cover" />

                          {/* Cover badge */}
                          {index === 0 && (
                            <div className="absolute left-1.5 top-1.5 px-1.5 py-0.5 rounded-md bg-[#F37020]/90 text-white text-[10px] font-bold font-admin shadow-lg">
                              Cover
                            </div>
                          )}

                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                            <button
                              type="button"
                              onClick={() => removeImage(imageUrl)}
                              disabled={!isSuperAdmin}
                              className="w-8 h-8 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/40 transition-colors inline-flex items-center justify-center disabled:opacity-50"
                              aria-label={`Remove image ${index + 1}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* Add more images placeholder */}
                      {draft.imageUrls.length < 8 && isSuperAdmin && (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          className="aspect-square rounded-lg border-2 border-dashed border-[#2A2A45] bg-[#0C0C12]/50 hover:border-[#2A2A3A] transition-colors flex flex-col items-center justify-center gap-1.5 disabled:opacity-40"
                        >
                          {uploading ? (
                            <Loader2 className="w-5 h-5 text-white/15 animate-spin" />
                          ) : (
                            <>
                              <Plus className="w-5 h-5 text-white/15" />
                              <span className="text-[10px] text-white/20 font-admin">Add more</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-[#2A2A45] flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 text-[11px] font-admin text-white/25">
                  {!draft.isActive && (
                    <span className="flex items-center gap-1 text-amber-400/70">
                      <EyeOff className="w-3 h-3" /> Hidden
                    </span>
                  )}
                  {draft.featured && (
                    <span className="flex items-center gap-1 text-amber-400/70">
                      <Star className="w-3 h-3" fill="currentColor" /> Featured
                    </span>
                  )}
                  {draft.imageUrls.length > 0 && (
                    <span>{draft.imageUrls.length} image{draft.imageUrls.length !== 1 ? "s" : ""}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 rounded-lg bg-white/[0.04] border border-[#2A2A45] text-sm text-white/50 hover:text-white/70 hover:bg-white/[0.06] transition-colors font-admin"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!canSave}
                    className={[
                      "inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all font-admin",
                      canSave
                        ? "bg-[#F37020] text-white hover:bg-[#E06010] shadow-lg shadow-[#F37020]/20"
                        : "bg-white/[0.04] border border-white/5 text-white/20 cursor-not-allowed",
                    ].join(" ")}
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saving ? "Saving..." : draft._id ? "Save Changes" : "Create Product"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
